










































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';
import {
  ITariffActions,
  ITariffGetters,
  ITariffMutations,
  ITariffState
} from '@/store/tariff';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import { percentageValidator, requiredValidator } from '@/utils/validators';

import {
  ITariff,
  ITariffData,
  SystemError,
  Tariff,
  TariffTypeEnum
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import CountryPicker from '../common/CountryPicker.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import TariffRegionAutoCompletePicker from './TariffRegionAutoCompletePicker.vue';
import HSCodeAutoCompletePicker from '../harmonized-system/HSCodeAutoCompletePicker.vue';

export default defineComponent({
  name: 'TariffFormDialog',

  components: {
    ALDialog,
    ErrorAlert,
    CountryPicker,
    TariffRegionAutoCompletePicker,
    HSCodeAutoCompletePicker
  },

  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    tariff: {
      type: Object as PropType<ITariffData | null>,
      default: null
    }
  },
  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const tariffData = ref<ITariffData>({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        tariffData.value = !props.isEdit
          ? ({
              tariff_type: TariffTypeEnum.Country
            } as ITariffData)
          : cloneDeep(props.tariff!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} tariff information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      ITariffState,
      ITariffGetters,
      ITariffActions,
      ITariffMutations
    >(store, 'tariff');

    const { createTariffRate, updateTariffRate } = useActions([
      'createTariffRate',
      'updateTariffRate'
    ]);
    const { getTariffTypeVisuals } = useGetters(['getTariffTypeVisuals']);

    const error = ref<ISystemError | null>(null);

    const isLoading = ref(false);

    function clearErrors() {
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function tariffTypeChangeHandler() {
      tariffData.value.origin_region = null;
      tariffData.value.origin_country = null;
      tariffData.value.hs_code = null;

      clearErrors();
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        isLoading.value = true;
        let tariffRateInstance: ITariff | null = null;

        try {
          tariffRateInstance = new Tariff(tariffData.value);
        } catch (err) {
          if (SystemError.isSystemError(err)) {
            error.value = err;
          }
        }

        if (tariffRateInstance != null) {
          const saveAction = props.isEdit ? updateTariffRate : createTariffRate;

          let payload = props.isEdit
            ? diffDeep(tariffRateInstance, props.tariff, true)
            : tariffRateInstance;

          saveAction(payload)
            .then(() => closeDialog())
            .catch(err => {
              error.value = setErrorFromAxiosError(err);
            })
            .finally(() => {
              isLoading.value = false;
            });
        }
      }
    }

    return {
      localDialog,
      tariffData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      isLoading,
      error,
      percentageValidator,
      requiredValidator,

      getTariffTypeVisuals,
      tariffTypeChangeHandler,
      TariffTypeEnum
    };
  }
});
